import React from 'react';
import { ChevronDown, Clock, Expand, Link2 } from 'lucide-react';
import type { Article, ArchitectureImage } from '../../data/profileData';
import { formatReadingTime } from '../../utils/readingTime';
import { Button, Card, Icon, LinkedInIcon, ResponsiveImage, Tag } from '../ui';

interface ArticleItemProps {
  article: Article;
  isOpen: boolean;
  featured?: boolean;
  onToggle: (article: Article) => void;
  onCopyLink: (article: Article) => void;
  onShare: (article: Article) => void;
  onOpenImage: (image: ArchitectureImage) => void;
}

const SUMMARY_LENGTH = 220;

export const formatArticleDate = (date: string): string => {
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const parsed = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(date);
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/** First paragraph of the article, trimmed to a short summary. */
export const summarize = (body: string): string => {
  const firstParagraph = body.trim().split(/\n\s*\n/)[0] ?? '';
  return firstParagraph.length > SUMMARY_LENGTH
    ? `${firstParagraph.slice(0, SUMMARY_LENGTH).replace(/\s+\S*$/, '')}…`
    : firstParagraph;
};

/** One article: a header that opens/closes it, and the full body with share actions when open. */
const ArticleItem: React.FC<ArticleItemProps> = ({
  article,
  isOpen,
  featured = false,
  onToggle,
  onCopyLink,
  onShare,
  onOpenImage,
}) => {
  const contentId = `article-content-${article.id}`;
  const HeadingTag = featured ? 'h3' : 'h4';

  return (
    <Card
      as="article"
      id={`article-${article.id}`}
      className={['article', featured && 'article--featured', isOpen && 'article--open'].filter(Boolean).join(' ')}
    >
      {featured && <p className="article__featured-label">Latest article</p>}
      <HeadingTag className="article__title">
        <button
          type="button"
          className="article__toggle"
          onClick={() => onToggle(article)}
          aria-expanded={isOpen}
          aria-controls={contentId}
        >
          <span>{article.title}</span>
          <Icon icon={ChevronDown} size={20} className="article__chevron" />
        </button>
      </HeadingTag>

      <div className="article__meta">
        <time dateTime={article.date}>{formatArticleDate(article.date)}</time>
        <span className="article__reading-time">
          <Icon icon={Clock} size={14} />
          {formatReadingTime(article.description)}
        </span>
        <ul className="article__tags" aria-label="Topics">
          {article.tags.map((tag) => (
            <li key={tag}>
              <Tag>{tag}</Tag>
            </li>
          ))}
        </ul>
      </div>

      {!isOpen && featured && <p className="article__summary">{summarize(article.description)}</p>}

      <div id={contentId} className="article__content" hidden={!isOpen}>
        {isOpen && (
          <>
            <div className="article__body">{article.description}</div>

            {article.images && article.images.length > 0 && (
              <div className="article__images">
                {article.images.map((image) => (
                  <figure key={image.src} className="article__figure">
                    <button
                      type="button"
                      className="article__image-button"
                      onClick={() => onOpenImage(image)}
                      aria-label={`Enlarge image: ${image.caption}`}
                    >
                      <ResponsiveImage path={image.src} alt={image.alt} className="article__image" />
                      <span className="article__image-hint" aria-hidden="true">
                        <Icon icon={Expand} size={16} />
                      </span>
                    </button>
                    <figcaption>{image.caption}</figcaption>
                  </figure>
                ))}
              </div>
            )}

            <div className="article__actions">
              <Button variant="secondary" size="sm" icon={<Icon icon={Link2} size={16} />} onClick={() => onCopyLink(article)}>
                Copy link
              </Button>
              <Button variant="ghost" size="sm" icon={<LinkedInIcon size={16} />} onClick={() => onShare(article)}>
                Share on LinkedIn
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default ArticleItem;
